import * as React from 'react';
import { HiUserGroup } from "react-icons/hi2";
import { IoClose, IoChevronDown, IoSearchOutline, IoCloseCircle } from "react-icons/io5";
import { toast } from 'react-toastify';
import { addNewTeam, getAllActiveMembers, getAllAdmins, logout, stringAvatar } from '../../../service/service-call';

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#262673] focus:ring-2 focus:ring-[#262673]/10";
const labelClass = "mb-1.5 block text-xs font-semibold text-gray-500";

function InitialsAvatar({ name, size = 24 }) {
  const { sx, children } = stringAvatar(name) || {};
  const bg = sx?.bgcolor || "#262673";
  return (
    <div
      style={{ width: size, height: size, backgroundColor: bg }}
      className="flex shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
    >
      {children}
    </div>
  );
}

export default function AddTeams({ addTeamsModal }) {
  const [allAdmins, setAllAdmins] = React.useState([]);
  const [allMembers, setAllMembers] = React.useState([]);
  const [tname, setTName] = React.useState('');
  const [selectedOption, setSelectedOption] = React.useState("");
  const [selectedTeamMembers, setSelectedTeamMembers] = React.useState([]);
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  const [leadOpen, setLeadOpen] = React.useState(false);
  const [memberOpen, setMemberOpen] = React.useState(false);
  const [memberSearch, setMemberSearch] = React.useState("");
  const leadRef = React.useRef(null);
  const memberRef = React.useRef(null);

  React.useEffect(() => {
    getAllAdmin();
    getAllMemberForTeam();
  }, []);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (leadRef.current && !leadRef.current.contains(e.target)) setLeadOpen(false);
      if (memberRef.current && !memberRef.current.contains(e.target)) setMemberOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function getAllAdmin() {
    getAllAdmins().then(resp => {
      resp.json().then(data => {
        setAllAdmins(data);
      });
    }).catch(error => {
      console.log("login user err " + error);
    });
  }

  function getAllMemberForTeam() {
    getAllActiveMembers().then(resp => {
      if (resp.status === 401) {
        logout();
      }
      resp.json().then(data => {
        setAllMembers(data);
      });
    }).catch(error => {
      console.log("login user err " + error);
    });
  }

  const handleTeamNameChange = (e) => setTName(e.target.value);

  const selectedLead = allAdmins.find((a) => a.id === selectedOption);

  const memberOptions = allMembers.map((option) => option.firstName + " " + option.lastName + " (" + option.id + ")");
  const filteredMemberOptions = memberOptions
    .filter((o) => !selectedTeamMembers.includes(o))
    .filter((o) => o.toLowerCase().includes(memberSearch.toLowerCase()));

  function addMember(option) {
    setSelectedTeamMembers((prev) => [...prev, option]);
    setMemberSearch("");
  }

  function removeMember(option) {
    setSelectedTeamMembers((prev) => prev.filter((o) => o !== option));
  }

  function addNewTeams() {
    let arr = [selectedTeamMemberIds];
    selectedTeamMembers.map(e => {
      let idCode = e.split("(");
      let val = idCode[1].split(")");
      if (!arr.includes(val[0].trim()))
        arr.push(val[0].trim());
    });

    if (tname === "" || tname === undefined || selectedOption === "" || selectedOption === undefined) {
      toast.error('Fields cannot be blank');
      return false;
    } else if (arr === "" || arr === undefined || arr.length === 0) {
      toast.error('Team members cannot be blank');
      return false;
    } else {
      setSubmitting(true);
      addNewTeam(tname, selectedOption, arr).then(resp => {
        setSubmitting(false);
        toast.success('Team added successfully');
        addTeamsModal();
      }).catch(error => {
        setSubmitting(false);
        if (error && error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message);
        } else if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.length > 0) {
          toast.error(error.response.data.errors[0]);
        } else {
          toast.error("Internal server error, contact support team");
        }
      });
    }
  }

  return (
    <div className="flex max-h-[85vh] flex-col">
      {/* Header */}
      <div className="relative border-b border-gray-100 px-6 pb-4 pt-6">
        <button
          onClick={addTeamsModal}
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <IoClose size={18} />
        </button>

        <div className="flex items-center gap-3 pr-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
            <HiUserGroup size={22} className="text-[#0B6BCB]" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">New team</p>
            <p className="text-sm font-bold text-[#0B6BCB]">Team Details</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="mb-4">
          <label className={labelClass}>
            Team Name <span className="text-red-400">*</span>
          </label>
          <input className={inputClass} type="text" maxLength={30} value={tname} onChange={handleTeamNameChange} />
          <p className="mt-1 text-right text-[10px] text-gray-300">{tname.length}/30</p>
        </div>

        {/* Team lead custom select */}
        <div className="relative mb-4" ref={leadRef}>
          <label className={labelClass}>
            Team Lead <span className="text-red-400">*</span>
          </label>
          <button
            type="button"
            onClick={() => setLeadOpen((p) => !p)}
            className={`${inputClass} flex items-center justify-between text-left`}
          >
            {selectedLead ? (
              <span className="flex items-center gap-2">
                <InitialsAvatar name={`${selectedLead.firstName} ${selectedLead.lastName}`} />
                {selectedLead.firstName} {selectedLead.lastName} ({selectedLead.id})
              </span>
            ) : (
              <span className="text-gray-400">Select team lead</span>
            )}
            <IoChevronDown size={14} className={`text-gray-400 transition-transform ${leadOpen ? "rotate-180" : ""}`} />
          </button>

          {leadOpen && (
            <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 shadow-xl">
              {allAdmins.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-400">No admins found</p>
              ) : (
                allAdmins.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setSelectedTeamMemberIds(item.id);
                      setSelectedOption(item.id);
                      setLeadOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <InitialsAvatar name={`${item.firstName} ${item.lastName}`} />
                    {item.firstName} {item.lastName} ({item.id})
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Team members multi-select with chips */}
        <div className="relative" ref={memberRef}>
          <label className={labelClass}>
            Add Team Members <span className="text-red-400">*</span>
          </label>

          <div
            onClick={() => setMemberOpen(true)}
            className="flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5 focus-within:border-[#262673] focus-within:ring-2 focus-within:ring-[#262673]/10"
          >
            {selectedTeamMembers.map((option) => (
              <span
                key={option}
                className="flex items-center gap-1.5 rounded-full bg-[#262673]/5 py-1 pl-1 pr-2 text-xs font-medium text-[#262673]"
              >
                <InitialsAvatar name={option.split(" (")[0]} size={18} />
                {option}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMember(option);
                  }}
                  className="text-[#262673]/50 hover:text-red-500"
                >
                  <IoCloseCircle size={13} />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              onFocus={() => setMemberOpen(true)}
              placeholder={selectedTeamMembers.length === 0 ? "Search members..." : ""}
              className="min-w-[100px] flex-1 border-none bg-transparent py-1 text-sm text-gray-800 outline-none"
            />
          </div>

          {memberOpen && (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 shadow-xl">
              {filteredMemberOptions.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-400">No matching members</p>
              ) : (
                filteredMemberOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => addMember(option)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <InitialsAvatar name={option.split(" (")[0]} />
                    {option}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end border-t border-gray-100 px-6 py-4">
        <button
          onClick={addNewTeams}
          disabled={submitting}
          className="flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
          Submit
        </button>
      </div>
    </div>
  );
}